import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { JsonAdvancedSearchResult, NodeServiceResourceService } from '@psnb/nwt-api';
import {
  BaseResponseDTO,
  FuelTankEntity,
  GeneratorSearch,
  GeneratorSearchDTO,
  PowerSupply,
  PowerSupplySearch,
  TelemetryActionRequest
} from '@psnb/psnb-api';
import {
  AuthService,
  GeneratorService,
  isDefinedNotNull,
  isDefinedNotNullNotEmpty,
  isUndefinedOrNull,
  isUndefinedOrNullOrEmpty,
  PowerSupplyListService,
  PowerSupplyService
} from '@psnb/services';
import { PowerSupplyMessageDialog } from '../../../common/message-dialog/power-supply-message-dialog';
import {
  DeployGeneratorViewModel,
  PowerSupplySearchDistance,
  PowerSupplySearchParams,
  SearchPowerSupply
} from './deploy-generator-dialog.model';
import { PowerSupplySelectionDialogComponent } from './power-supply-selection-dialog.component';
import { SearchDataQrScannerDialogComponent } from './qr-scanner-dialog.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'psnb-searchdata-deploy-generator-dialog',
  styleUrls: ['./deploy-generator-dialog.component.scss'],
  templateUrl: './deploy-generator-dialog.component.html'
})
export class SearchDataDeployGeneratorDialogComponent implements OnDestroy, OnInit {
  public vm: DeployGeneratorViewModel;

  @ViewChild('fuelTankIDAutoTrigger', { read: MatAutocompleteTrigger }) private fuelTankAutocompleteTrigger: MatAutocompleteTrigger;
  @ViewChild('nodeAutoTrigger', { read: MatAutocompleteTrigger }) private nodeAutocompleteTrigger: MatAutocompleteTrigger;

  private readonly destroyed$: Subject<void>;
  private readonly slowDebounceTimeInMs: number = 750;

  private createdTimeValue: string;
  private extendedRuntimeFuelTank: string;
  private fuelTankDataResponse: FuelTankEntity;
  private generatorDataResponse: GeneratorSearchDTO;
  private macAddress: string = '';
  private powerSupplyId: number | undefined;
  private powerSupplyMessageDialogRef: MatDialogRef<PowerSupplyMessageDialog> | undefined;
  private powerSupplySelectionDialogRef: MatDialogRef<PowerSupplySelectionDialogComponent> | undefined;
  private qrScannerDialogRef: MatDialogRef<SearchDataQrScannerDialogComponent> | undefined;
  private selectedFuelTankId: string;
  private selectedGeneratorId: string;
  private userName: string;

  public constructor(
    private authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private dialogRef: MatDialogRef<SearchDataDeployGeneratorDialogComponent>,
    private generatorService: GeneratorService,
    private nodeService: NodeServiceResourceService,
    @Inject(MAT_DIALOG_DATA) private powerSupplyData: {
      dbId?: number;
      localId?: string;
      qrCodeAssetId?: string;
      serialNumber?: string;
    },
    private powerSupplyListService: PowerSupplyListService,
    private powerSupplyService: PowerSupplyService
  ) {
    this.createdTimeValue = new Date().toISOString();
    this.destroyed$ = new Subject<void>();
  }

  /**
   * Initializes the component.
   * Sets up the view model, coordinate watchers, and search subscriptions.
   */
  public ngOnInit(): void {
    this.initializeViewModel();
    this.setupCoordinateWatchers();
    this.setupFuelTankSearch();
    this.setupNodeSearch();
    this.setupPowerSupplyNameSearch();
    this.setupMacAddressSearch();
  }

  /**
   * Cleans up subscriptions and closes any open dialogs.
   */
  public ngOnDestroy(): void {
    if (
      isDefinedNotNull(this.dialogRef?.getState) &&
      this.dialogRef.getState() !== 0
    ) {
      /* empty */
    } else {
      this.dialogRef?.close(false);
    }
    this.powerSupplyMessageDialogRef?.close();
    this.powerSupplySelectionDialogRef?.close();
    this.qrScannerDialogRef?.close();
    this.destroyed$?.next();
    this.destroyed$?.complete();
  }

  /**
   * Initializes the view model with default values.
   */
  private initializeViewModel(): void {
    this.vm = {
      comments: '',
      deployGeneratorButtonDisabled: true,
      formGroup: this.createFormGroup(),
      fuelTankAssetSearchResult: [],
      generatorAssetSearchResult: [],
      generatorLocalId: this.powerSupplyData.localId,
      generatorQrCodeAssetId: this.powerSupplyData.qrCodeAssetId,
      generatorSerialNumber: this.powerSupplyData.serialNumber,
      isLoadingLocation: false,
      isLoadingMacData: false,
      isLoadingNodeData: false,
      isNodeSearchButtonEnabled: false,
      latitudeValue: undefined,
      longitudeValue: undefined,
      macSearchResult: [],
      nodeSearchResult: [],
      noPowerSupplyFound: false,
      powerSupplyNameSearchResult: [],
      selectedPowerSupplyName: '',
      serialNumberSearchResult: []
    };

    this.userName = `${this.authService.getUserDetails.firstName} ${this.authService.getUserDetails.lastName}`;

    // Auto-populate generator data if provided
    if (isDefinedNotNull(this.powerSupplyData.dbId)) {
      this.selectedGeneratorId = this.powerSupplyData.dbId.toString();
      this.fetchGeneratorDataById(this.selectedGeneratorId);
    }
  }

  /**
   * Sets up watchers for latitude and longitude form controls.
   * Clears selected power supply when both coordinates are empty.
   */
  private setupCoordinateWatchers(): void {
    this.vm.formGroup.get('latitudeControl').valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((): void => {
        const latValue: string = this.vm.formGroup.get('latitudeControl')?.value;
        const longValue: string = this.vm.formGroup.get('longitudeControl')?.value;
        if (isUndefinedOrNullOrEmpty(latValue) && isUndefinedOrNullOrEmpty(longValue)) {
          this.resetSelectedPowerSupplyState();
        }
      });

    this.vm.formGroup.get('longitudeControl').valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((): void => {
        const latValue: string = this.vm.formGroup.get('latitudeControl')?.value;
        const longValue: string = this.vm.formGroup.get('longitudeControl')?.value;
        if (isUndefinedOrNullOrEmpty(latValue) && isUndefinedOrNullOrEmpty(longValue)) {
          this.resetSelectedPowerSupplyState();
        }
      });
  }

  /**
   * Sets up search subscription for fuel tank ID field.
   */
  private setupFuelTankSearch(): void {
    this.setupSearchSubscription(
      this.vm.formGroup.get('fuelTankIDSearchTerm'),
      (fuelTankData: string | GeneratorSearch): void => {
        if (typeof fuelTankData === 'string' && isDefinedNotNullNotEmpty(fuelTankData)) {
          this.generatorService
            .advanceSearchByAssetID(fuelTankData)
            .pipe(take(1), takeUntil(this.destroyed$))
            .subscribe((response: GeneratorSearch[]): void => {
              this.vm.fuelTankAssetSearchResult = response.filter(
                (item: GeneratorSearch): boolean => isDefinedNotNull(item.fuelTankId) && item.assetType === 'Fuel Tank'
              );
              this.cdr.detectChanges();
              if (this.vm.fuelTankAssetSearchResult.length > 0 && this.fuelTankAutocompleteTrigger) {
                this.fuelTankAutocompleteTrigger.openPanel();
              }
            });
        }
      }
    );
  }

  /**
   * Sets up search subscription for node field.
   * Also monitors the search term to enable/disable the search button.
   */
  private setupNodeSearch(): void {
    this.setupSearchSubscription(
      this.vm.formGroup.get('nodeSearchTerm'),
      (nodeData: string): void => {
        if (typeof nodeData === 'string' && isDefinedNotNullNotEmpty(nodeData)) {
          this.nodeService.resourceNodeServiceResourceAdvancedSearchGET(nodeData.trim())
            .pipe(take(1), takeUntil(this.destroyed$))
            .subscribe((response: JsonAdvancedSearchResult[]): void => {
              this.vm.nodeSearchResult = response;
              this.cdr.detectChanges();
              if (this.vm.nodeSearchResult.length > 0 && this.nodeAutocompleteTrigger) {
                this.nodeAutocompleteTrigger.openPanel();
              }
            });
        } else {
          this.vm.nodeSearchResult = [];
          this.resetSelectedPowerSupplyState();
        }
      }
    );

    // Monitor node search term changes to enable/disable the search button
    const nodeSearchControl: AbstractControl | null = this.vm.formGroup.get('nodeSearchTerm');
    if (isDefinedNotNull(nodeSearchControl)) {
      nodeSearchControl.valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe((value: string): void => {
          this.vm.isNodeSearchButtonEnabled = typeof value === 'string' && value.trim().length >= 4;
          this.cdr.detectChanges();
        });
    }
  }

  /**
   * Sets up search subscription for power supply name field.
   */
  private setupPowerSupplyNameSearch(): void {
    this.setupSearchSubscription(
      this.vm.formGroup.get('powerSupplyNameSearchTerm'),
      (psNameData: string): void => {
        if (typeof psNameData === 'string' && isDefinedNotNullNotEmpty(psNameData)) {
          this.powerSupplyService.advanceSearchByPowerSupplyName(psNameData)
            .pipe(take(1), takeUntil(this.destroyed$))
            .subscribe((response: PowerSupplySearch[]): void => {
              this.vm.powerSupplyNameSearchResult = response;
              this.cdr.detectChanges();
            });
        } else if (isUndefinedOrNullOrEmpty(psNameData)) {
          // Reset only when psNameData is empty (not when it's a selected object)
          this.vm.powerSupplyNameSearchResult = [];
          this.resetSelectedPowerSupplyState();
        }
      }
    );
  }

  /**
   * Sets up search subscription for MAC address field.
   * Formats MAC address input to standard colon-separated format.
   */
  private setupMacAddressSearch(): void {
    this.setupSearchSubscription(
      this.vm.formGroup.get('macSearchTerm'),
      (macData: string): void => {
        if (isDefinedNotNullNotEmpty(macData) && typeof macData === 'string') {
          const remcolon: string = macData.toString().replace(/:/g, '');
          const remcolonupdate: string = remcolon.replace(/\s/g, '');

          // Guard against empty strings after removing separators
          if (isUndefinedOrNullOrEmpty(remcolonupdate)) {
            this.vm.isLoadingMacData = false;
            this.vm.macSearchResult = [];
            this.cdr.detectChanges();
            return;
          }

          this.vm.isLoadingMacData = true;
          this.cdr.detectChanges();

          const blocks: string[] | null = remcolonupdate.match(/.{1,2}/g);
          if (isUndefinedOrNull(blocks) || blocks.length === 0) {
            this.vm.isLoadingMacData = false;
            this.vm.macSearchResult = [];
            this.cdr.detectChanges();
            return;
          }

          const firstBlock: string | undefined = blocks.shift();
          if (isUndefinedOrNull(firstBlock)) {
            this.vm.isLoadingMacData = false;
            this.vm.macSearchResult = [];
            this.cdr.detectChanges();
            return;
          }

          let formattedMac: string = firstBlock;
          for (const block of blocks) {
            formattedMac = `${formattedMac}:${block}`;
          }
          this.macAddress = formattedMac;

          this.powerSupplyService.advancePowerSupplyMacSearch(this.macAddress.trim())
            .pipe(take(1), takeUntil(this.destroyed$))
            .subscribe((response: SearchPowerSupply[]): void => {
              this.vm.isLoadingMacData = false;
              this.vm.macSearchResult = response;
              this.cdr.detectChanges();
            });
        } else if (isUndefinedOrNullOrEmpty(macData)) {
          // Reset only when macData is empty (not when it's a selected object)
          this.vm.isLoadingMacData = false;
          this.vm.macSearchResult = [];
          this.resetSelectedPowerSupplyState();
        }
      }
    );
  }

  /**
   * Gets the asset ID from a GeneratorSearch option.
   * Returns assetId, localId, or a stringified generatorId/fuelTankId in order of precedence.
   *
   * @param {GeneratorSearch | string} option - The generator search option or a string value
   * @returns {string | undefined} The asset ID or undefined
   */
  public getAssetID(option: GeneratorSearch | string): string | undefined {
    if (typeof option === 'string') {
      return option;
    }

    if (isDefinedNotNull(option?.assetId)) {
      return option.assetId;
    } else if (isDefinedNotNull(option?.localId)) {
      return option.localId;
    } else {
      return option?.generatorId?.toString() ?? option?.fuelTankId?.toString();
    }
  }

  /**
   * Gets the combined generator identifier display string.
   * Combines qrCodeAssetId, localId, and dbId with "/" separator, only showing values that exist.
   *
   * @returns {string} The combined identifier string
   */
  public getGeneratorIdentifier(): string {
    const parts: string[] = [];

    if (isDefinedNotNullNotEmpty(this.vm.generatorQrCodeAssetId)) {
      parts.push(this.vm.generatorQrCodeAssetId);
    }

    if (isDefinedNotNullNotEmpty(this.vm.generatorLocalId)) {
      parts.push(this.vm.generatorLocalId);
    }

    if (isDefinedNotNull(this.powerSupplyData.dbId)) {
      parts.push(this.powerSupplyData.dbId.toString());
    }

    return parts.join(' / ');
  }

  /**
   * Creates and initializes the form group with all required form controls.
   *
   * @returns {FormGroup} The initialized form group
   */
  private createFormGroup(): FormGroup {
    return new FormGroup({
      comments: new FormControl(''),
      fuelTankIDSearchTerm: new FormControl(undefined),
      latitudeControl: new FormControl('', [Validators.required]),
      longitudeControl: new FormControl('', [Validators.required]),
      macSearchTerm: new FormControl(''),
      nodeSearchTerm: new FormControl(''),
      powerSupplyNameSearchTerm: new FormControl('')
    });
  }

  /**
   * Resets the deploy button state by clearing generator data and disabling the button.
   */
  private resetDeployButton(): void {
    this.generatorDataResponse = undefined;
    this.vm.deployGeneratorButtonDisabled = true;
    this.cdr.detectChanges();
  }

  /**
   * Resets the selected power supply state.
   * Clears the selected power supply name, power supply ID, and the "no power supply found" flag.
   * Triggers change detection after clearing.
   */
  private resetSelectedPowerSupplyState(): void {
    this.vm.selectedPowerSupplyName = '';
    this.vm.noPowerSupplyFound = false;
    this.powerSupplyId = undefined;
    this.cdr.detectChanges();
  }

  /**
   * Handles the cancel action.
   * Closes the dialog with a false result.
   */
  public onCancel(): void {
    this.dialogRef.close(false);
  }

  /**
   * Deploys the selected generator.
   * Validates that a generator is selected, builds the payload, and calls the generator service.
   * Shows success or error dialogs based on the API response.
   */
  public deployGenerator(): void {
    if (!this.validateDeploymentRequirements()) {
      return;
    }

    const payload: TelemetryActionRequest = this.buildDeploymentPayload();
    this.generatorService
      .deployGenerator(payload)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe({
        error: (): void => this.showErrorDialog('Failed to deploy generator. Please try again.'),
        next: (deployResponse: BaseResponseDTO): void => this.handleDeploymentResponse(deployResponse)
      });
  }

  /**
   * Validates that all requirements for deployment are met.
   *
   * @returns {boolean} True if all requirements are met, false otherwise
   */
  private validateDeploymentRequirements(): boolean {
    if (isUndefinedOrNull(this.selectedGeneratorId)) {
      this.showErrorDialog('Please select a Generator before confirming.');
      return false;
    }

    if (isUndefinedOrNullOrEmpty(this.vm.selectedPowerSupplyName)) {
      this.showErrorDialog('Please select a Power Supply before confirming.');
      return false;
    }

    if (isUndefinedOrNull(this.powerSupplyId) || this.powerSupplyId === 0) {
      this.showErrorDialog('Please select a valid Power Supply before confirming.');
      return false;
    }

    return true;
  }

  /**
   * Builds the deployment payload from current component state.
   *
   * @returns {TelemetryActionRequest} The telemetry action request payload
   */
  private buildDeploymentPayload(): TelemetryActionRequest {
    return {
      address: this.generatorDataResponse?.address,
      city: this.generatorDataResponse?.city,
      comments: this.vm.formGroup.get('comments')?.value,
      createDate: this.createdTimeValue,
      createdByUserId: this.userName,
      currentType: this.generatorDataResponse?.currentType,
      division: this.generatorDataResponse?.division,
      extendedRuntimeFuelTank: this.extendedRuntimeFuelTank,
      fuelTankId: this.selectedFuelTankId,
      generatorChain: this.generatorDataResponse?.generatorChain,
      generatorCord: this.generatorDataResponse?.generatorCord,
      generatorId: this.selectedGeneratorId,
      generatorLastModified: this.createdTimeValue,
      generatorLastModifiedBy: this.userName,
      generatorLock: this.generatorDataResponse?.generatorLock,
      generatorManufactureId: this.generatorDataResponse?.generatorManufactureId,
      generatorManufacturer: this.generatorDataResponse?.generatorManufacturer,
      generatorModel: this.generatorDataResponse?.generatorModel,
      generatorModelId: this.generatorDataResponse?.generatorModelId,
      generatorRuntimeHours: this.generatorDataResponse?.generatorRuntimeHours,
      generatorSerialNumber: this.generatorDataResponse?.generatorSerialNumber,
      generatorStatus: this.generatorDataResponse?.generatorStatus,
      generatorStatusId: this.generatorDataResponse?.generatorStatusId,
      latitude: this.generatorDataResponse?.latitude,
      loanedTeamName: this.generatorDataResponse?.loanedTeamName,
      localId: this.generatorDataResponse?.localId,
      longitude: this.generatorDataResponse?.longitude,
      outputVoltage: this.generatorDataResponse?.outputVoltage,
      powerSupplyId: this.powerSupplyId,
      qrCodeId: this.generatorDataResponse?.qrCodeId,
      region: this.generatorDataResponse?.region,
      state: this.generatorDataResponse?.state,
      teamName: this.generatorDataResponse?.teamName,
      zipCode: this.generatorDataResponse?.zipcode
    };
  }

  /**
   * Handles the deployment response from the API.
   *
   * @param {BaseResponseDTO} deployResponse - The deployment response
   */
  private handleDeploymentResponse(deployResponse: BaseResponseDTO): void {
    if (deployResponse?.success) {
      this.showSuccessDialog('Generator deployed successfully!', (): void => {
        this.dialogRef.close(true);
      });
    } else {
      const errorMessage: string = deployResponse?.message ?? 'Failed to deploy generator. Please try again.';
      this.showErrorDialog(errorMessage);
    }
  }

  /**
   * Shows an error dialog with the specified message.
   *
   * @param {string} message - The error message to display
   */
  private showErrorDialog(message: string): void {
    if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
      this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
        data: { message }
      });
      this.powerSupplyMessageDialogRef
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((): void => {
          this.powerSupplyMessageDialogRef = undefined;
        });
    }
  }

  /**
   * Shows a success dialog with the specified message.
   *
   * @param {string} message - The success message to display
   * @param {function} onClose - Optional callback to execute when dialog closes
   */
  private showSuccessDialog(message: string, onClose?: () => void): void {
    if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
      this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
        data: { message }
      });
      this.powerSupplyMessageDialogRef
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((): void => {
          this.powerSupplyMessageDialogRef = undefined;
          if (isDefinedNotNull(onClose)) {
            onClose();
          }
        });
    }
  }

  /**
   * Handles fuel tank selection from the autocomplete.
   * Validates the selected option is a fuel tank and fetches its data.
   *
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event
   */
  public searchByFuelTankId(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value.generatorId || event.option.value.trailerId) {
      if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
        this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
          data: { message: 'please select a Fuel Tank' }
        });
        this.powerSupplyMessageDialogRef
          .afterClosed()
          .pipe(take(1), takeUntil(this.destroyed$))
          .subscribe((): void => {
            this.powerSupplyMessageDialogRef = undefined;
          });
      }
      return;
    } else {
      this.selectedFuelTankId = event.option.value.fuelTankId;
    }
    this.fetchFuelTankDataById(this.selectedFuelTankId);
  }

  /**
   * Fetches generator data by ID and updates the view model.
   *
   * @param {string} generatorId - The generator ID to fetch
   */
  private fetchGeneratorDataById(generatorId: string): void {
    this.generatorService
      .generatorSearchByAssetId(generatorId)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe({
        error: (): void => {
          if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
            this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
              data: { message: 'Error retrieving generator data' }
            });
            this.powerSupplyMessageDialogRef
              .afterClosed()
              .pipe(take(1), takeUntil(this.destroyed$))
              .subscribe((): void => {
                this.powerSupplyMessageDialogRef = undefined;
              });
          }
        },
        next: (generatorResponse: GeneratorSearchDTO[]): void => {
          this.vm.deployGeneratorButtonDisabled = false;
          this.generatorDataResponse = generatorResponse[0];
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * Fetches fuel tank data by ID and updates the extended runtime value.
   *
   * @param {string} fuelTankId - The fuel tank ID to fetch
   */
  private fetchFuelTankDataById(fuelTankId: string): void {
    this.generatorService
      .fuelTankSearchById(fuelTankId)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe({
        error: (): void => {
          if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
            this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
              data: { message: 'Error retrieving fuel tank data' }
            });
            this.powerSupplyMessageDialogRef
              .afterClosed()
              .pipe(take(1), takeUntil(this.destroyed$))
              .subscribe((): void => {
                this.powerSupplyMessageDialogRef = undefined;
              });
          }
        },
        next: (fuelTankResponse: FuelTankEntity[]): void => {
          this.fuelTankDataResponse = fuelTankResponse[0];
          this.extendedRuntimeFuelTank = this.fuelTankDataResponse.capacity;
        }
      });
  }

  /**
   * Sets up a debounced search subscription on a form control.
   *
   * @param {AbstractControl} control - The form control to watch
   * @param {function} searchFn - The search function to execute when the control value changes
   */
  private setupSearchSubscription(control: AbstractControl, searchFn: (searchResultData: string) => void): void {
    control.valueChanges
      .pipe(
        debounceTime(this.slowDebounceTimeInMs),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((searchSubscriptionData: string): void => {
        if (isDefinedNotNull(searchSubscriptionData)) {
          searchFn(searchSubscriptionData);
        }
      });
  }

  /**
   * Opens QR scanner dialog for fuel tank asset ID.
   * Sets the scanned value to the fuel tank search field when successful.
   */
  public openFuelTankScanner(): void {
    this.vm.fuelTankAssetSearchResult = [];
    this.vm.formGroup.get('fuelTankIDSearchTerm')?.setValue(undefined);
    this.cdr.detectChanges();

    if (isUndefinedOrNull(this.qrScannerDialogRef)) {
      this.qrScannerDialogRef = this.dialog.open(SearchDataQrScannerDialogComponent, {
        data: { title: 'Scan Fuel Tank QR Code' },
        disableClose: false,
        width: '500px'
      });

      this.qrScannerDialogRef
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((scannedValue: string): void => {
          this.qrScannerDialogRef = undefined;
          if (isDefinedNotNullNotEmpty(scannedValue)) {
            const fuelTankControl: AbstractControl | null = this.vm.formGroup.get('fuelTankIDSearchTerm');
            if (isDefinedNotNull(fuelTankControl)) {
              fuelTankControl.setValue(scannedValue);
              this.cdr.detectChanges();
            }
          }
        });
    }
  }

  /**
   * Gets the node name for display in autocomplete.
   *
   * @param {JsonAdvancedSearchResult | string} option - The node search result or a string value
   * @returns {string | undefined} The formatted node name or undefined
   */
  public getNodeName(option: JsonAdvancedSearchResult | string): string | undefined {
    if (typeof option === 'string') {
      return option;
    }

    if (isDefinedNotNull(option)) {
      return `${option.node} / ${option.nodeCmts}`;
    }

    return undefined;
  }

  /**
   * Handles node selection from autocomplete.
   * Calls getPowerSupplyList with the selected node details and opens the power supply selection dialog.
   *
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event
   */
  public nodeSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedValue: JsonAdvancedSearchResult = event.option.value;
    if (isDefinedNotNull(selectedValue?.nodeId)) {
      // Clear other search fields
      this.vm.formGroup.get('powerSupplyNameSearchTerm')?.setValue('', { emitEvent: false });
      this.vm.formGroup.get('macSearchTerm')?.setValue('', { emitEvent: false });
      this.vm.formGroup.get('latitudeControl')?.reset('', { emitEvent: false });
      this.vm.formGroup.get('longitudeControl')?.reset('', { emitEvent: false });
      this.vm.powerSupplyNameSearchResult = [];
      this.vm.macSearchResult = [];
      this.vm.selectedPowerSupplyName = '';
      this.vm.noPowerSupplyFound = false;
      this.cdr.detectChanges();

      // Call getPowerSupplyList with the selected node details
      const searchParams: PowerSupplySearchParams = {
        address: undefined,
        distance: PowerSupplySearchDistance.Default,
        headendOrCommunity: undefined,
        latitude: undefined,
        longitude: undefined,
        macAddress: undefined,
        node: selectedValue.node,
        nodeCommunintyServed: selectedValue.nodeCommunintyServed,
        nodeId: selectedValue.nodeId,
        nodeText: undefined,
        powerSupplyId: undefined,
        powerSupplyName: undefined,
        regionId: undefined
      };

      this.powerSupplyListService.getPowerSupplyList(searchParams)
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((powerSupplyList: PowerSupply[]): void => {
          if (isDefinedNotNullNotEmpty(powerSupplyList)) {
            this.vm.nodeSearchResult = [];
            this.cdr.detectChanges();
            this.openPowerSupplySelectionDialog(powerSupplyList, 'Select Power Supply');
          } else {
            this.vm.selectedPowerSupplyName = '';
            this.vm.noPowerSupplyFound = true;
            this.vm.nodeSearchResult = [];
            this.cdr.detectChanges();
          }
        });
    }
  }

  /**
   * Handles node search icon click.
   * Searches for nodes matching the current input, then finds power supplies for the matching nodes.
   * Opens the power supply selection dialog if results are found.
   */
  public searchNodeIcon(): void {
    const nodeSearchValue: string = this.vm.formGroup.get('nodeSearchTerm')?.value;

    if (isUndefinedOrNullOrEmpty(nodeSearchValue) || typeof nodeSearchValue !== 'string') {
      return;
    }

    // Clear other search fields
    this.vm.formGroup.get('powerSupplyNameSearchTerm')?.setValue('', { emitEvent: false });
    this.vm.formGroup.get('macSearchTerm')?.setValue('', { emitEvent: false });
    this.vm.formGroup.get('latitudeControl')?.reset('', { emitEvent: false });
    this.vm.formGroup.get('longitudeControl')?.reset('', { emitEvent: false });
    this.vm.powerSupplyNameSearchResult = [];
    this.vm.macSearchResult = [];
    this.vm.selectedPowerSupplyName = '';
    this.vm.noPowerSupplyFound = false;
    this.vm.isLoadingNodeData = true;
    this.cdr.detectChanges();

    // First, get the node details by searching for the node text
    this.nodeService.resourceNodeServiceResourceAdvancedSearchGET(nodeSearchValue.trim())
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe((nodeResults: JsonAdvancedSearchResult[]): void => {
        if (isDefinedNotNullNotEmpty(nodeResults)) {
          // Use the first matching node result
          const selectedNode: JsonAdvancedSearchResult = nodeResults[0];

          // Call getPowerSupplyList with the node details
          const searchParams: PowerSupplySearchParams = {
            address: undefined,
            distance: PowerSupplySearchDistance.Default,
            headendOrCommunity: undefined,
            latitude: undefined,
            longitude: undefined,
            macAddress: undefined,
            node: selectedNode.node,
            nodeCommunintyServed: selectedNode.nodeCommunintyServed,
            nodeId: selectedNode.nodeId,
            nodeText: undefined,
            powerSupplyId: undefined,
            powerSupplyName: undefined,
            regionId: undefined
          };

          this.powerSupplyListService.getPowerSupplyList(searchParams)
            .pipe(take(1), takeUntil(this.destroyed$))
            .subscribe((powerSupplyList: PowerSupply[]): void => {
              this.vm.isLoadingNodeData = false;
              if (isDefinedNotNullNotEmpty(powerSupplyList)) {
                this.vm.nodeSearchResult = [];
                this.cdr.detectChanges();
                this.openPowerSupplySelectionDialog(powerSupplyList, 'Select Power Supply');
              } else {
                this.vm.selectedPowerSupplyName = '';
                this.vm.noPowerSupplyFound = true;
                this.vm.nodeSearchResult = [];
                this.cdr.detectChanges();
              }
            });
        } else {
          // No nodes found
          this.vm.isLoadingNodeData = false;
          this.vm.selectedPowerSupplyName = '';
          this.vm.noPowerSupplyFound = true;
          this.vm.nodeSearchResult = [];
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * Gets the power supply name for display in autocomplete.
   *
   * @param {PowerSupplySearch | string} option - The power supply search result or a string value
   * @returns {string | undefined} The power supply name or undefined
   */
  public getPowerSupplyName(option: PowerSupplySearch | string): string | undefined {
    if (typeof option === 'string') {
      return option;
    }

    return option?.powerSupplyName;
  }

  /**
   * Handles power supply name selection from autocomplete.
   * Updates the power supply ID for deployment.
   *
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event
   */
  public searchByPowerSupplyName(event: MatAutocompleteSelectedEvent): void {
    const selectedPowerSupply: PowerSupplySearch = event.option.value;
    if (isDefinedNotNull(selectedPowerSupply?.powerSupplyName)) {
      // Clear other search fields
      this.vm.formGroup.get('nodeSearchTerm')?.setValue('', { emitEvent: false });
      this.vm.formGroup.get('macSearchTerm')?.setValue('', { emitEvent: false });
      this.vm.formGroup.get('latitudeControl')?.reset('', { emitEvent: false });
      this.vm.formGroup.get('longitudeControl')?.reset('', { emitEvent: false });
      this.vm.nodeSearchResult = [];
      this.vm.macSearchResult = [];

      const searchParams: PowerSupplySearchParams = {
        address: undefined,
        distance: PowerSupplySearchDistance.Default,
        headendOrCommunity: undefined,
        latitude: undefined,
        longitude: undefined,
        macAddress: undefined,
        node: undefined,
        nodeCommunintyServed: undefined,
        nodeId: undefined,
        nodeText: undefined,
        powerSupplyId: undefined,
        powerSupplyName: selectedPowerSupply.powerSupplyName,
        regionId: undefined
      };

      this.powerSupplyListService.getPowerSupplyList(searchParams)
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((powerSupplyList: PowerSupply[]): void => {
          if (isDefinedNotNullNotEmpty(powerSupplyList)) {
            const powerSupply: PowerSupply = powerSupplyList[0];
            this.powerSupplyId = powerSupply.powerSupplyId;
            this.vm.selectedPowerSupplyName = powerSupply.powerSupplyName ?? '';
            this.vm.noPowerSupplyFound = false;
            this.cdr.detectChanges();
          } else {
            this.vm.selectedPowerSupplyName = '';
            this.vm.noPowerSupplyFound = true;
            this.cdr.detectChanges();
          }
        });
    }
  }

  /**
   * Gets the MAC address for display in autocomplete.
   *
   * @param {SearchPowerSupply | string} option - The power supply search result or a string value
   * @returns {string | undefined} The MAC address or undefined
   */
  public getMacAddress(option: SearchPowerSupply | string): string | undefined {
    if (typeof option === 'string') {
      return option;
    }

    return option?.mac;
  }

  /**
   * Handles MAC address selection from autocomplete.
   * Updates the power supply ID for deployment.
   *
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event
   */
  public searchByMacAddress(event: MatAutocompleteSelectedEvent): void {
    const selectedPowerSupply: SearchPowerSupply = event.option.value;
    if (isDefinedNotNull(selectedPowerSupply?.mac)) {
      this.macAddress = '';
      this.macAddress = selectedPowerSupply.mac ?? '';

      // Clear other search fields
      this.vm.formGroup.get('nodeSearchTerm')?.setValue('', { emitEvent: false });
      this.vm.formGroup.get('powerSupplyNameSearchTerm')?.setValue('', { emitEvent: false });
      this.vm.formGroup.get('latitudeControl')?.reset('', { emitEvent: false });
      this.vm.formGroup.get('longitudeControl')?.reset('', { emitEvent: false });
      this.vm.nodeSearchResult = [];
      this.vm.powerSupplyNameSearchResult = [];

      const searchParams: PowerSupplySearchParams = {
        address: undefined,
        distance: PowerSupplySearchDistance.Default,
        headendOrCommunity: undefined,
        latitude: undefined,
        longitude: undefined,
        macAddress: this.macAddress,
        node: undefined,
        nodeCommunintyServed: undefined,
        nodeId: undefined,
        nodeText: undefined,
        powerSupplyId: undefined,
        powerSupplyName: undefined,
        regionId: undefined
      };

      this.powerSupplyListService.getPowerSupplyList(searchParams)
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((powerSupplyList: PowerSupply[]): void => {
          if (isDefinedNotNullNotEmpty(powerSupplyList)) {
            const powerSupply: PowerSupply = powerSupplyList[0];
            this.powerSupplyId = powerSupply.powerSupplyId;
            this.vm.selectedPowerSupplyName = powerSupply.powerSupplyName ?? '';
            this.vm.noPowerSupplyFound = false;
            this.cdr.detectChanges();
          } else {
            this.vm.selectedPowerSupplyName = '';
            this.vm.noPowerSupplyFound = true;
            this.cdr.detectChanges();
          }
        });
    }
  }

  /**
   * Validates and updates latitude/longitude values.
   */
  public searchByLocation(): void {
    const latControl: AbstractControl | undefined = this.vm.formGroup.get('latitudeControl');
    const longControl: AbstractControl | undefined = this.vm.formGroup.get('longitudeControl');
    if (isDefinedNotNull(latControl) && isDefinedNotNull(longControl) &&
        latControl.valid && longControl.valid) {

      // Clear other search fields
      this.vm.formGroup.get('nodeSearchTerm')?.setValue('', { emitEvent: false });
      this.vm.formGroup.get('powerSupplyNameSearchTerm')?.setValue('', { emitEvent: false });
      this.vm.formGroup.get('macSearchTerm')?.setValue('', { emitEvent: false });
      this.vm.nodeSearchResult = [];
      this.vm.powerSupplyNameSearchResult = [];
      this.vm.macSearchResult = [];
      this.vm.selectedPowerSupplyName = '';
      this.vm.noPowerSupplyFound = false;

      this.vm.isLoadingLocation = true;
      this.vm.latitudeValue = latControl.value;
      this.vm.longitudeValue = longControl.value;
      this.cdr.detectChanges();

      const searchParams: PowerSupplySearchParams = {
        address: undefined,
        distance: PowerSupplySearchDistance.Default,
        headendOrCommunity: undefined,
        latitude: this.vm.latitudeValue,
        longitude: this.vm.longitudeValue,
        macAddress: undefined,
        node: undefined,
        nodeCommunintyServed: undefined,
        nodeId: undefined,
        nodeText: undefined,
        powerSupplyId: undefined,
        powerSupplyName: undefined,
        regionId: undefined
      };

      this.powerSupplyListService.getPowerSupplyList(searchParams)
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((powerSupplyList: PowerSupply[]): void => {
          this.vm.isLoadingLocation = false;
          if (isDefinedNotNullNotEmpty(powerSupplyList)) {
            this.openPowerSupplySelectionDialog(powerSupplyList, 'Select Power Supply');
          } else {
            this.vm.selectedPowerSupplyName = '';
            this.vm.noPowerSupplyFound = true;
            this.cdr.detectChanges();
          }
        });
    }
  }

  /**
   * Gets current geolocation and populates latitude/longitude fields.
   */
  public getLocation(): void {
    if (isDefinedNotNull(navigator.geolocation)) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition): void => {
          const latControl: AbstractControl | null = this.vm.formGroup.get('latitudeControl');
          const longControl: AbstractControl | null = this.vm.formGroup.get('longitudeControl');

          if (isDefinedNotNull(latControl) && isDefinedNotNull(longControl)) {
            latControl.setValue(position.coords.latitude.toFixed(6));
            longControl.setValue(position.coords.longitude.toFixed(6));
            this.vm.latitudeValue = position.coords.latitude;
            this.vm.longitudeValue = position.coords.longitude;
            this.cdr.detectChanges();
          }
        }
      );
    } else {
      if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
        this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
          data: { message: 'Geolocation is not supported by this browser.' }
        });
        this.powerSupplyMessageDialogRef
          .afterClosed()
          .pipe(take(1), takeUntil(this.destroyed$))
          .subscribe((): void => {
            this.powerSupplyMessageDialogRef = undefined;
          });
      }
    }
  }

  /**
   * Filters out special characters for MAC address input.
   *
   * @param {KeyboardEvent} event - The keyboard event
   * @returns {boolean} Whether the character is allowed
   */
  public omitSpecialChar(event: KeyboardEvent): boolean {
    const charCode: number = event.charCode;
    return ((charCode > 64 && charCode < 91) || (charCode > 96 && charCode < 123) ||
            charCode === 8 || charCode === 32 || (charCode >= 48 && charCode <= 58));
  }

  /**
   * TrackBy function for fuel tank list to optimize change detection.
   *
   * @param {number} index - The index of the item
   * @param {GeneratorSearch} asset - The fuel tank search asset
   * @returns {string | number} The asset ID for tracking, or index as fallback
   */
  public trackByFuelTank(index: number, asset: GeneratorSearch): string | number {
    return asset?.assetId ?? index;
  }

  /**
   * TrackBy function for node list to optimize change detection.
   *
   * @param {number} index - The index of the item
   * @param {JsonAdvancedSearchResult} node - The node search result
   * @returns {number} The node ID for tracking, or index as fallback
   */
  public trackByNode(index: number, node: JsonAdvancedSearchResult): number {
    return node?.nodeId ?? index;
  }

  /**
   * TrackBy function for power supply name list to optimize change detection.
   *
   * @param {number} index - The index of the item
   * @param {PowerSupplySearch} powerSupply - The power supply search result
   * @returns {number} The power supply ID for tracking, or index as fallback
   */
  public trackByPowerSupply(index: number, powerSupply: PowerSupplySearch): number {
    return powerSupply?.powerSupplyId ?? index;
  }

  /**
   * TrackBy function for MAC address list to optimize change detection.
   *
   * @param {number} index - The index of the item
   * @param {SearchPowerSupply} mac - The power supply search result with MAC
   * @returns {string | number} The MAC address for tracking, or index as fallback
   */
  public trackByMac(index: number, mac: SearchPowerSupply): string | number {
    return mac?.mac ?? index;
  }

  /**
   * Opens the power supply selection dialog with a list of power supplies.
   * Sets the selected power supply ID and name when user makes a selection.
   *
   * @param {PowerSupply[]} powerSupplyList - The list of power supplies to display
   * @param {string} title - The dialog title
   */
  private openPowerSupplySelectionDialog(powerSupplyList: PowerSupply[], title: string): void {
    if (isUndefinedOrNull(this.powerSupplySelectionDialogRef)) {
      this.powerSupplySelectionDialogRef = this.dialog.open(PowerSupplySelectionDialogComponent, {
        data: {
          powerSupplyList,
          title
        },
        disableClose: false
      });

      this.powerSupplySelectionDialogRef
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((selectedPowerSupply: PowerSupply | undefined): void => {
          this.powerSupplySelectionDialogRef = undefined;
          if (isDefinedNotNull(selectedPowerSupply)) {
            this.powerSupplyId = selectedPowerSupply.powerSupplyId;
            this.vm.selectedPowerSupplyName = selectedPowerSupply.powerSupplyName ?? '';
            this.vm.noPowerSupplyFound = false;
          } else {
            this.vm.selectedPowerSupplyName = '';
            this.vm.noPowerSupplyFound = false;
          }
          this.cdr.detectChanges();
        });
    }
  }
}
